import { inject, Pipe, PipeTransform } from '@angular/core';
import { LogsService } from '@shared/services/logs';

/**
 * A custom Angular Pipe used to extract the YouTube video ID from a given YouTube video URL.
 * This pipe takes a valid YouTube video URL as input and extracts the video ID using URL parsing.
 *
 * ## Example Usage
 * In an Angular template:
 * ```html
 * {{ 'https://www.youtube.com/watch?v=dQw4w9WgXcQ' | youtubeIdExtractor }}
 * ```
 * Output:
 * ```
 * dQw4w9WgXcQ
 * ```
 *
 * ## Details
 * - If the URL contains a `v` query parameter (as in `https://www.youtube.com/watch?v=...`),
 *   the value of this parameter is extracted.
 * - If the URL does not contain a `v` query parameter, the pipe attempts to extract the
 *   video ID from the last segment of the URL's pathname (e.g., `https://youtu.be/...`).
 * - Logs success or failure using the `LogsService`.
 *
 * ## Error Handling
 * - If the URL is invalid or a YouTube video ID cannot be determined, an empty string (`''`) is returned.
 * - Errors are logged using the `LogsService.error` method.
 *
 * ## Dependencies
 * This class uses an external `LogsService` to log debugging and error information.
 * Make sure to include and configure the `LogsService` properly in the application if used.
 *
 * ## Methods
 * - `transform(youtubeVideoUrl: string): string`
 *
 * @example
 * ```typescript
 * const youtubeIdExtractorPipe = new YoutubeIdExtractorPipe();
 * const videoId = youtubeIdExtractorPipe.transform('https://www.youtube.com/watch?v=dQw4w9WgXcQ');
 * console.log(videoId); // Output: 'dQw4w9WgXcQ'
 * ```
 *
 * @export
 * @class YoutubeIdExtractorPipe
 * @implements {PipeTransform}
 */
@Pipe({
  name: 'youtubeIdExtractor',
})
export class YoutubeIdExtractorPipe implements PipeTransform {
  private readonly logs = inject(LogsService);

  transform(youtubeVideoUrl: string): string {
    try {
      const url = new URL(youtubeVideoUrl);
      const paramsV = url.searchParams.get('v');
      if (paramsV) {
        this.logs.debug('YoutubeIdExtractorPipe', `The YouTube video ${youtubeVideoUrl} ID is ${paramsV}`);
        return paramsV;
      }
      const id = url.pathname.split('/').pop();
      if (id) {
        this.logs.debug('YoutubeIdExtractorPipe', `The YouTube video ${youtubeVideoUrl} ID is ${id}`);
        return id;
      }
      this.logs.error('YoutubeIdExtractorPipe', `Failed to extract youtube id from url ${youtubeVideoUrl}`);
      return '';
    } catch (error) {
      this.logs.error('YoutubeIdExtractorPipe', 'Failed to extract youtube id', error);
      return youtubeVideoUrl;
    }
  }
}
